import React, { useContext, useState } from 'react';
import {
  InputField,
  RadioGroupField,
  SelectField
} from '@trp-beacon-ui-kit/react-controls';
import { states } from '../ContactInformationScreen/utils';
import { FlowContext } from '../../core/flow-engine';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import Heading from '../../components/Typography/Heading/Heading';
import {
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  valueRequired,
  zipCodeValidation,
  cityValidation,
  stateValidation,
  trustnameValidation,
  normalizeSSN,
  normalizeEIN
} from '../../flows/shared/validations';
import { handleInputFormat } from '../../flows/shared/utils';
import styles from './style.css';
import { isValidEIN, isValidSSN } from '../../flows/shared/testers';
import { withDefault } from '../../core/util';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

const TrustInformationScreen = () => {
  const { back, next, formProps, flowProps, appState } = useContext(
    FlowContext
  );
  const { title = 'Enter Your Trust Information' } = flowProps;
  const {
    store: { app }
  } = appState;
  const { trust } = app;

  const defaultTINState = () => {
    if (trust && !trust?.taxIDSSN) {
      return {
        type: 'EIN',
        placeholder: '00-0000000',
        maxLength: '10',
        format: normalizeEIN
      };
    }

    return {
      type: 'SSN',
      placeholder: '000-00-0000',
      maxLength: '11',
      format: normalizeSSN
    };
  };

  const [TIN, setTIN] = useState(defaultTINState);
  const [trustDateValue, setTrustDateValue] = useState(
    withDefault(trust?.trustDate, '')
  );

  const handleTINUpdate = event => {
    const radioValueIsSSN = event?.target?.value === 'SSN';
    const trustIDField = document.getElementById('trustTaxID-field') || {};

    if (radioValueIsSSN) {
      trustIDField.value = normalizeSSN(trustIDField?.value);
      return setTIN({
        type: event.target.value,
        placeholder: '000-00-0000',
        maxLength: '11',
        format: () => normalizeSSN(trustIDField?.value)
      });
    }
    trustIDField.value = normalizeEIN(trustIDField?.value);
    return setTIN({
      type: event.target.value,
      placeholder: '00-0000000',
      maxLength: '10',
      format: () => normalizeEIN(trustIDField?.value)
    });
  };

  // handle formatting trustTaxID value here instead of beacon component due to dynamic taxID type choice
  // this would require a rerender of the input component otherwise to change formatter property
  // type choices are SSN or EIN, and each formatted differently (SSN: '123-45-6789' vs EIN: '12-3456789')
  const formatTrustTaxID = event => {
    const trustIDField = document.getElementById('trustTaxID-field') || {};
    const formattedValue = TIN?.format(event?.target?.value);
    trustIDField.value = formattedValue;
  };

  // handle validation here due to dynamic taxID type
  // validation regex check differs based on chosen taxID type (SSN vs EIN)
  const taxIDValidation = (val, context = {}) => {
    const { trustIDType } = context;
    if (trustIDType === 'EIN') {
      return isValidEIN(val);
    }
    if (trustIDType === 'SSN') {
      return isValidSSN(val);
    }
    return false;
  };

  const radioGroupOptions = () => {
    if (trust && !trust?.taxIDSSN) {
      return [
        {
          title: 'SSN',
          value: 'SSN'
        },
        {
          title: 'EIN',
          value: 'EIN',
          defaultChecked: true
        }
      ];
    }

    return [
      {
        title: 'SSN',
        value: 'SSN',
        defaultChecked: true
      },
      {
        title: 'EIN',
        value: 'EIN'
      }
    ];
  };

  return (
    <>
      <div className="container">
        <Heading text={title} />
        <div className="trust-name">
          <InputField
            type="text"
            title="Trust Name"
            {...formProps.trustName}
            validation={trustnameValidation}
            maxLength="70"
            className="blocked_data"
            data-name={generateGeneralTestID('Trust', 'FullName')}
          />
        </div>
        <div className="address-fields">
          <div className="address-line1">
            <InputField
              title="Address Line 1"
              {...formProps.trustStreet1}
              validation={[
                addressLine1Validation,
                poBoxLine1Validation,
                valueRequired
              ]}
              maxLength="35"
              className="blocked_data"
              data-name={generateGeneralTestID(
                'Trust',
                'TrustResidentialStreetOne'
              )}
            />
          </div>

          <div className="address-line2">
            <InputField
              title="Address Line 2"
              {...formProps.trustStreet2}
              validation={[addressLine2Validation, poBoxLine2Validation]}
              maxLength="35"
              className="blocked_data"
              data-name={generateGeneralTestID(
                'Trust',
                'TrustResidentialStreetTwo'
              )}
            />
          </div>
        </div>
        <div className="address-fields">
          <div className="city-input">
            <InputField
              title="City"
              {...formProps.trustCity}
              validation={[cityValidation, valueRequired]}
              className="blocked_data"
              data-name={generateGeneralTestID('Trust', 'TrustResidentialCity')}
            />
          </div>
          <div className="zipcode-input">
            <InputField
              title="Zip Code"
              {...formProps.trustZip}
              validation={[zipCodeValidation, valueRequired]}
              maxLength="5"
              className="blocked_data"
              data-name={generateGeneralTestID('Trust', 'TrustResidentialZip')}
            />
          </div>
          <div className="state-input">
            <SelectField
              title="State"
              {...formProps.trustState}
              validation={[stateValidation, valueRequired]}
              data-name={generateGeneralTestID(
                'Trust',
                'TrustResidentialState'
              )}>
              <option hidden value="">
                State
              </option>
              {states.map(abrr => {
                return (
                  <option
                    key={abrr}
                    value={abrr}
                    data-name={generateGeneralTestID('Trust', `State_${abrr}`)}>
                    {abrr}
                  </option>
                );
              })}
            </SelectField>
          </div>
        </div>
        <div className="personal-info-fields">
          <div className="dob-input">
            <InputField
              style={{ paddingRight: '8px' }}
              type="text"
              title="Date of Trust"
              aria-describedby="hint"
              maxLength="10"
              onChange={evt =>
                handleInputFormat(evt, 'DATE', setTrustDateValue)
              }
              value={trustDateValue}
              placeholder="mm/dd/yyyy"
              {...formProps.trustDate}
              className="blocked_data"
            />
            <dd id="hint" style={{ display: 'none' }}>
              This is a required field. Please enter a two digit month, a two
              digit day and a four digit year for the Date of Trust value.
            </dd>
          </div>
          <div className="ssn-input">
            <InputField
              title={
                <div className="tax-id-input-title">
                  <RadioGroupField
                    title="Trust Identification Number"
                    onChange={handleTINUpdate}
                    options={radioGroupOptions()}
                    name="trustIDType"
                  />
                </div>
              }
              id="trustTaxID-field"
              data-name={generateGeneralTestID('Trust', 'TrustTaxID')}
              placeholder={TIN && TIN?.placeholder}
              maxLength={TIN && TIN?.maxLength}
              validation={taxIDValidation}
              onChange={formatTrustTaxID}
              {...formProps.trustID}
              className="blocked_data"
            />
          </div>
        </div>
      </div>

      <div className="btn-container">
        <MFBeaconButton
          variant="primary"
          size="normal"
          id="trustPersonalInformationContinue"
          className="continue-btn"
          onClick={next()}
          label="Continue"
          data-name={generateGeneralTestID('Trust', 'ContinueButton')}
        />
        <MFBeaconButton
          variant="secondary"
          size="normal"
          id="trustPersonalInformationBack"
          onClick={back()}
          label="Back"
          data-name={generateGeneralTestID('Trust', 'BackButton')}
        />
      </div>

      <PhoneNumber />

      <style jsx>{styles}</style>
    </>
  );
};

export default TrustInformationScreen;
